import React, { Children, createContext, isValidElement, useContext, useState } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'

import styles from './Tabs.module.scss'

const TabContext = createContext({
  activeTab: '',
  setActiveTab: null
})

const useTabContext = () => useContext(TabContext)

const getTabs = (children, type) => {
  return Children.toArray(children).filter((child, el) => {
    const isValidChild = isValidElement(child)

    if (!isValidChild) {
      return false
    }

    if (child.type !== TabPanel) {
      return false
    }

    return Boolean(child.props.name)
  })
}

function getDefaultActiveTab(children, activeTab) {
  const defaultActiveTab = activeTab

  if (activeTab) {
    return defaultActiveTab
  }

  const [child] = children

  if (!child) {
    return defaultActiveTab
  }

  return child.props.name
}

export const TabPanel = ({ name, children }) => {
  const { activeTab } = useTabContext()
  const visibilityStyle = { display: `${name !== activeTab ? 'none' : 'block'}` }
  return (
    <div role='tabpanel' style={visibilityStyle} className={styles.TabPanel}>
      {children}
    </div>
  )
}

TabPanel.propTypes = {
  /**
   * Name of the tab, also used as tab heading
   */
  name: PropTypes.string.isRequired
}

const Tab = (props) => {
  return <a {...props} role='tab' />
}

const TabList = ({ children }) => {
  const { activeTab, setActiveTab } = useTabContext()

  return (
    <ul className={styles.TabList} role='tablist'>
      {Children.map(children, (child) => {
        const { name } = child.props
        const isActive = name === activeTab

        return (
          <li className={styles.TabItem}>
            <Tab
              className={cx(styles.Tab, { [styles.TabActive]: isActive })}
              title={name}
              onClick={() => setActiveTab(name)}
            >
              {name}
            </Tab>
          </li>
        )
      })}
    </ul>
  )
}

const Tabs = (props) => {
  const children = getTabs(props.children)

  const [internalActiveTabName, setInternalActiveTab] = useState(getDefaultActiveTab(children, props.activeTab))
  const activeTab = props.activeTab ?? internalActiveTabName

  const setActiveTab = (name) => {
    if (props.activeTab && props.onChange) {
      props.onChange(name)
      return
    }

    setInternalActiveTab(name)
  }

  const context = {
    activeTab,
    setActiveTab
  }

  return (
    <TabContext.Provider value={context}>
      <div className={styles.Tabs}>
        <TabList>{children}</TabList>
        <div>{children}</div>
      </div>
    </TabContext.Provider>
  )
}

Tabs.propTypes = {
  /**
   * Children/Tab content
   */
  children: PropTypes.node,
  /**
   * Children/Tab content
   */
  activeTab: PropTypes.string,
  /**
   * Change handler for switching tabs
   */
  onChange: PropTypes.func
}

Tabs.defaultProps = {
  activeTab: null
}

export default Tabs
